import { AdminProjects } from '@platform/client';
import { Icon, PillBadge, PillBadgeColors } from '@platform/components';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import cx from 'classix';
import { AdminProject } from 'lib/client/src/platformapi.schemas';
import { useMemo, useState, useContext } from 'react';
import FlexibleLink from '../../../util/FlexibleLink';
import ProjectPublishStageModal from './ProjectPublishStageModal';
import { getDaysSince } from '../../../util/DateHelpers';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminListPageWrapper } from '../../AdminListPage';

const columnHelper = createColumnHelper<AdminProject>();

export const ProjectsAdmin = () => {
  return (
    <AdminListPageWrapper title={'Projects'}>
      <ProjectsAdminContent />
    </AdminListPageWrapper>
  );
};

const ProjectsAdminContent = () => {
  const { adminQueryOptions } = useContext(AdminTableContext);
  const [publishStageModalIsOpen, setpublishStageModalIsOpen] = useState<boolean>(false);
  const [publishStageModalProject, setPublishStageModalProject] = useState<
    AdminProject | undefined
  >();

  const getBadgeColor = (stage: string, daysSinceUpdate: number) => {
    let badgeColor = 'gray';
    if (stage === 'published') {
      badgeColor = 'success-500';
    } else if (stage === 'review' || stage === 'revision_requested') {
      badgeColor = stage === 'review' && daysSinceUpdate >= 6 ? 'error' : 'warning';
    }
    return badgeColor;
  };

  const {
    data: adminProjects = [],
    isError,
    isLoading,
    isSuccess,
  } = AdminProjects.useGetProjects(adminQueryOptions, {
    query: {
      placeholderData: keepPreviousData,
    },
  });
  const { data: adminProjectsCount = 0 } = AdminProjects.useGetAdminProjectCount({
    filter: adminQueryOptions.filter,
  });

  const handlePublishStageEditClick = (row: AdminProject) => {
    setPublishStageModalProject(row);
    setpublishStageModalIsOpen(true);
  };

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          cell: (id) => id.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>ID</span>,
          size: 25,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('name', {
          cell: ({ row }) => {
            return (
              <FlexibleLink
                type='legacy'
                href={`/admin/browse/project/${row.original.id}`}
                className='text-brand-500 hover:underline'
              >
                {row.original.name}
              </FlexibleLink>
            );
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Name</span>,
          size: 150,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('organizationName', {
          cell: ({ row }) => {
            return (
              <FlexibleLink
                type='legacy'
                href={`/organization/${row.original.organizationId}`}
                className='text-brand-500 hover:underline'
              >
                {row.original.organizationName}
              </FlexibleLink>
            );
          },
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Organization Name</span>
          ),
          size: 150,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('representativeName', {
          cell: (representativeName) => representativeName.getValue(),
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Representative Name</span>
          ),
          size: 100,
          enableSorting: false,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('expertises', {
          cell: (skills) => {
            return skills.getValue().map((skill: string) => (
              <PillBadge variant='plain' color='indigo' key={skill} size='sm'>
                {skill}
              </PillBadge>
            ));
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Skills</span>,
          size: 100,
          enableSorting: false,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('openToTeam', {
          cell: (openToTeam) => (openToTeam.getValue() ? 'Yes' : 'No'),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Teams</span>,
          size: 50,
          enableSorting: false,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('matchNetworks', {
          cell: (matchNetworks) => {
            return matchNetworks.getValue().map((network: string) => (
              <PillBadge variant='plain' color='blue' key={network} size='sm'>
                {network}
              </PillBadge>
            ));
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Match Networks</span>,
          size: 25,
          enableSorting: false,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('publishingStage', {
          cell: ({ row, cell }) => {
            const stage = cell.getValue();
            const daysSinceUpdate = getDaysSince(row.original.publishingStageUpdatedDate);
            const badgeColor = getBadgeColor(stage, daysSinceUpdate);
            return (
              <div className='flex justify-between align-middle'>
                <PillBadge variant='dot' size='md' color={badgeColor as PillBadgeColors}>
                  {stage}
                </PillBadge>
                {stage === 'review' && (
                  <p
                    className={cx(
                      stage === 'review' && daysSinceUpdate >= 6 && 'font-bold text-error-600',
                    )}
                  >
                    ({daysSinceUpdate} days)
                  </p>
                )}
                <button onClick={() => handlePublishStageEditClick(row.original)}>
                  <Icon type='edit-01' size='14' />
                </button>
              </div>
            );
          },
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Publishing Stage</span>
          ),
          size: 25,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('stage', {
          cell: (stage) => stage.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Match Stage</span>,
          size: 25,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('createdAt', {
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleDateString(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Created</span>,
          size: 25,
          meta: {
            filterVariant: 'date-select',
          },
        }),
        columnHelper.display({
          id: 'actions',
          cell: ({ row }) => {
            return (
              <div className='flex flex-col gap-1'>
                <FlexibleLink
                  href={`/projects/review/${row.original.id}`}
                  newTab={true}
                  type='internal'
                  className='text-brand-500 hover:underline'
                >
                  Review
                </FlexibleLink>
                <FlexibleLink
                  href={`/setup/opportunity/${row.original.id}/details`}
                  newTab={true}
                  type='legacy'
                  className='text-brand-500 hover:underline'
                >
                  Edit
                </FlexibleLink>
              </div>
            );
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Actions</span>,
          size: 50,
        }),
      ] as ColumnDef<AdminProject>[],
    [],
  );

  return (
    <>
      <AdminTable
        data={adminProjects}
        columns={columns}
        filteredCount={adminProjectsCount}
        isLoading={isLoading}
        isError={isError}
        isSuccess={isSuccess}
        enableFiltering={true}
      />
      <ProjectPublishStageModal
        project={publishStageModalProject as AdminProject}
        isOpen={publishStageModalIsOpen}
        onClose={() => {
          setpublishStageModalIsOpen(false);
        }}
        page={adminQueryOptions.page}
        limit={adminQueryOptions.limit}
      />
    </>
  );
};
